import { UtilsService } from './../../../../../core/utils/utils.service';
import { ServiceGroupPhotoCategory } from './../../../../../service-groups/service-group.model';
import { SettingsService } from './../../../../../settings/settings.service';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Job, JobPhoto } from '../../job.model';
import { Attachment } from '../../../../../core/attachment/attachment.model';
import { LoggerService } from '../../../../../core/logger/logger.service';
import { Project } from '../../../../project.model';
import { ProjectService } from '../../../../project.service';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../../../core/notification/notification.service';
import { AttachmentService } from '../../../../../core/attachment/attachment.service';
import { CanSeePhotoDateSetting } from '../../../../../core/shared-models/license-type.model';
import { TranslateService } from '@ngx-translate/core';
import { CdkDragDrop, CdkDragEnter } from '@angular/cdk/drag-drop';

@Component({
    selector: 'app-job-photos',
    templateUrl: './job-photos.component.html',
    styleUrls: ['./job-photos.component.scss'],
})
export class JobPhotosComponent implements OnInit, OnChanges, OnDestroy {
    @Output() changed = new EventEmitter<Job>();
    @Input() job: Job;
    @Input() changesAllowed = false;
    project: Project;
    categories: ServiceGroupPhotoCategory[];
    baseCategories: ServiceGroupPhotoCategory[];
    showPhotoDate = CanSeePhotoDateSetting.ONLY_DATE;

    fileTypeLabels: any = {
        image: this.translate.instant('projects.jobs.details.file-types.image'),
        video: this.translate.instant('projects.jobs.details.file-types.video'),
        audio: this.translate.instant('projects.jobs.details.file-types.audio'),
        pdf: this.translate.instant('projects.jobs.details.file-types.pdf'),
    };

    getTranslatedFileTypes(fileTypes: string[]): string {
        return fileTypes.map((type) => this.fileTypeLabels[type] || type).join(', ');
    }

    currentProjectSubscription: Subscription;
    currentServiceGroupSubscription: Subscription;

    src: string | Blob = undefined;
    dragTargetAllowed = true;
    altPressed = false;

    @HostListener('window:keydown.alt') onAltDown() {
        this.altPressed = true;
    }

    @HostListener('window:keyup.alt') onAltUp() {
        this.altPressed = false;
    }

    constructor(
        private projectSrv: ProjectService,
        private notifySrv: NotificationService,
        private logger: LoggerService,
        private settingsSrv: SettingsService,
        public utils: UtilsService,
        private attachmentSrv: AttachmentService,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.currentProjectSubscription = this.projectSrv.currentProject$.subscribe((proj) => {
            this.project = proj;
        });
        this.currentServiceGroupSubscription = this.projectSrv.currentServiceGroup$.subscribe((sg) => {
            this.baseCategories = this.utils.clone(sg.settings.dataRecording.photoCategories);
            this.updateCategories();
        });

        // only being admin or when this setting is true will the mobile user see image times
        this.showPhotoDate = this.settingsSrv.getUserRights().canSeePhotoDate;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.job && this.baseCategories) {
            this.updateCategories();
        }
    }

    ngOnDestroy() {
        if (this.currentProjectSubscription) {
            this.currentProjectSubscription.unsubscribe();
        }
        if (this.currentServiceGroupSubscription) {
            this.currentServiceGroupSubscription.unsubscribe();
        }
    }

    /**
     * Update categories based on the service group settings
     * @private
     */
    private updateCategories() {
        if (!this.baseCategories || !this.job) {
            return;
        }

        this.categories = this.utils.clone(this.baseCategories);

        // Check our job data if we have categories no longer present in the service group and add them locally
        // for display. This may have happened due to editing the service group after data collection was done
        const categoriesWithPhotos = new Set(this.job.photos.map((p) => p.photoCategoryName));
        categoriesWithPhotos.forEach((photoCategoryName) => {
            if (this.categories.findIndex((c) => c.categoryName == photoCategoryName) == -1) {
                this.categories.push(new ServiceGroupPhotoCategory({ categoryName: photoCategoryName, readonly: true }));
            }
        });
    }

    /**
     * Triggered when the user adds an photo by selecting an attachment
     */
    async onAddPhoto(photo: Attachment | Attachment[], photoCategoryName: string) {
        this.logger.log('[JOB PHOTOS] added job photo', photo);

        // photo limit
        if (this.job.photos.length >= 50) {
            this.notifySrv.warn(
                this.translate.instant('projects.jobs.details.article.photo-limit-desc'),
                this.translate.instant('projects.jobs.details.article.photo-limit-title'),
                false,
                10000
            );
            return;
        }

        if (Array.isArray(photo)) {
            // this.src = photo[0].filePath;

            for (let ph of photo) {
                let photoModel = new JobPhoto(ph);
                photoModel.photoCategoryName = photoCategoryName;
                this.job.photos.push(photoModel);
            }
        } else {
            // this.src = photo.filePath;

            if (this.utils.isNative) {
                this.src = await this.getNativeUri(true, photo);
            } else {
                let localFile = await this.attachmentSrv.readAttachmentFromDisk(photo).toPromise();
                if (typeof localFile.fileData === 'string') {
                    this.src = localFile.fileData;
                } else {
                    this.src = URL.createObjectURL(localFile.fileData as Blob);
                }
            }

            let photoModel = new JobPhoto(photo);
            photoModel.photoCategoryName = photoCategoryName;
            this.job.photos.push(photoModel);
        }

        this.changed.emit(this.job);
    }

    // private checkFileExists() {
    //     return this.attachmentSrv.checkIfAttachmentFileExists(this._attachment);
    // }

    /**
     * Retrieve URI for local file
     * @param uriRemap defaults to true which leads to a Capacitor URI remap to its hybrid URLs when running native. No effect for web contexts. Setting it false prevents such remapping and gets plain file:// URIs
     */
    private getNativeUri(uriRemap = true, att) {
        return this.attachmentSrv.getUriForAttachment(att, uriRemap);
    }

    /**
     * Tries to load the attachment content from disk
     * Returns a promise
     */
    private loadAttachmentFromDisk(att) {
        return this.attachmentSrv.readAttachmentFromDisk(att, true).toPromise();
    }

    /**
     * Triggered when the user deletes an photo by deleting an attachment
     * @param photo
     */
    onRemovePhoto(photo: Attachment) {
        this.logger.log('[JOB PHOTOS] removed job photo', photo);
        let photoIndex = this.job.photos.findIndex((x) => x._id === photo._id);
        if (photoIndex > -1) {
            this.job.photos.splice(photoIndex, 1);
        }

        this.changed.emit(this.job);
    }

    onPhotoUpdated(photo: Attachment) {
        this.logger.log('[JOB PHOTOS] updated job photo', photo._id);
        let photoIndex = this.job.photos.findIndex((x) => x._id === photo._id);
        if (photoIndex > -1) {
            let newPhoto = new JobPhoto(photo);
            newPhoto.photoCategoryName = this.job.photos[photoIndex].photoCategoryName;
            this.job.photos[photoIndex] = newPhoto;
        }

        this.changed.emit(this.job);
    }

    getPhotosByCategory(categoryName: string): JobPhoto[] {
        return this.job.photos.filter((p) => p.photoCategoryName === categoryName);
    }

    /**
     * Handle photo drop from CDK drag-and-drop.
     */
    onDropPhoto(event: CdkDragDrop<any>, targetCategoryName: string) {
        const { photo, sourceArticleFormGroup } = event.item.data;

        // Check if file type is allowed
        if (event.previousContainer !== event.container) {
            const targetCategory = this.categories?.find((c) => c.categoryName === targetCategoryName);
            if (targetCategory && !this.attachmentSrv.isFileTypeAllowed(photo.fileMimeType, targetCategory.fileType)) {
                this.notifySrv.warn(this.translate.instant('core.attachment.errors.file-type-not-allowed-err'));
                return;
            }
        }

        try {
            if (event.previousContainer === event.container) {
                // reorder within category
                if (event.previousIndex === event.currentIndex) return; // position hasn't changed
                this.logger.log('[JOB PHOTOS] Reorder within category');
                const categoryPhotos = this.getPhotosByCategory(targetCategoryName);
                const from = this.job.photos.indexOf(categoryPhotos[event.previousIndex]);
                const to = this.job.photos.indexOf(categoryPhotos[event.currentIndex]);
                this.job.photos.splice(to, 0, ...this.job.photos.splice(from, 1));
                this.changed.emit(this.job);
                return;
            } else {
                // move to another category or component
                this.logger.log('[JOB PHOTOS] Move attachment to other category or component');
                // Remove from source
                if (sourceArticleFormGroup) {
                    const sourcePhotos = sourceArticleFormGroup.get('photos');
                    const idx = sourcePhotos.controls.findIndex((ctrl) => ctrl.value._id === photo._id);
                    if (idx > -1) {
                        sourcePhotos.removeAt(idx);
                        sourceArticleFormGroup.markAsDirty();
                    }
                } else {
                    const idx = this.job.photos.findIndex((p) => p._id === photo._id);
                    if (idx > -1) this.job.photos.splice(idx, 1);
                }

                // Add to job.photos
                const newPhoto = new JobPhoto({ ...photo, photoCategoryName: targetCategoryName });
                const targetPhotos = this.job.photos.flatMap((p, i) => (p.photoCategoryName === targetCategoryName ? [i] : []));
                const insertAt = targetPhotos[event.currentIndex] ?? this.job.photos.length;
                this.job.photos.splice(insertAt, 0, newPhoto);

                this.changed.emit(this.job);
            }
        } catch (error) {
            this.logger.error('[JOB PHOTOS] Error moving attachment to other category or component', error);
            this.notifySrv.error(this.translate.instant('core.attachment.errors.move-attachment-err'));
        }
    }

    onDragEntered(event: CdkDragEnter<any>) {
        const photo = event.item.data?.photo;
        const targetFileType = event.container.data?.fileType;
        if (photo && targetFileType) {
            this.dragTargetAllowed = this.attachmentSrv.isFileTypeAllowed(photo.fileMimeType, targetFileType);
        } else {
            this.dragTargetAllowed = true;
        }
    }

    /**
     * Native HTML5 drag & drop support (used to drag photos between different browser windows).
     * @param event
     * @param photo
     * @param sourceCategoryName
     */

    onPhotoDragStart(event: DragEvent, photo: JobPhoto, sourceCategoryName: string) {
        if (!this.changesAllowed || !event.dataTransfer) {
            return;
        }
        event.dataTransfer.setData(
            'application/x-dokupit-attachment',
            JSON.stringify({
                jobId: this.job._id,
                projectId: this.project._id,
                sourceCategoryName,
                photo: this.utils.clone(photo),
            })
        );
    }

    async onCategoryNativeDrop(event: DragEvent, categoryName: string) {
        const rawPhotoPayload = event.dataTransfer?.getData('application/x-dokupit-attachment');
        if (!rawPhotoPayload || !this.changesAllowed) {
            return; // not our own drag payload (e.g. a real OS file) - nothing for us to do here
        }
        event.preventDefault();
        await this.handleNativePhotoDrop(rawPhotoPayload, categoryName);
    }

    /**
     * handle actual drop of native drag payload
     * @param rawPhotoPayload
     * @param targetCategoryName
     * @private
     */
    private async handleNativePhotoDrop(rawPhotoPayload: string, targetCategoryName: string) {
        let payload: { jobId: string; projectId: string; sourceCategoryName: string; photo: JobPhoto };
        try {
            payload = JSON.parse(rawPhotoPayload);
        } catch (err) {
            this.logger.error('[JOB PHOTOS] Could not parse native drag payload', err);
            return;
        }

        const targetCategory = this.categories?.find((c) => c.categoryName === targetCategoryName);
        if (targetCategory && !this.attachmentSrv.isFileTypeAllowed(payload.photo.fileMimeType, targetCategory.fileType)) {
            this.notifySrv.warn(this.translate.instant('core.attachment.errors.file-type-not-allowed-err'));
            return;
        }

        if (this.job.photos.length >= 50) {
            this.notifySrv.warn(
                this.translate.instant('projects.jobs.details.article.photo-limit-desc'),
                this.translate.instant('projects.jobs.details.article.photo-limit-title'),
                false,
                10000
            );
            return;
        }

        try {
            const newPhoto: JobPhoto = this.utils.duplicateItem(payload.photo);
            newPhoto.fileName = newPhoto.fileName.replace(payload.photo._id, newPhoto._id);
            newPhoto.filePath = `${this.project._id}/jobs/${this.job._id}/${newPhoto.fileName}`;
            newPhoto.photoCategoryName = targetCategoryName;

            await this.attachmentSrv.duplicateAttachment(payload.photo.filePath, newPhoto.filePath);

            this.job.photos.push(newPhoto);
            this.changed.emit(this.job);
        } catch (error) {
            this.logger.error('[JOB PHOTOS] Error duplicating dragged photo', error);
            this.notifySrv.error(this.translate.instant('core.attachment.errors.move-attachment-err'));
        }
    }
}
